import React, { useState, useMemo } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import moment from "moment";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
);

const CATEGORIES = [
  {
    key: "appRides",
    label: "App Rides",
    borderColor: "#F4A261",
    bgColor: "rgba(244, 162, 97, 0.25)",
    pillBg: "#fff",
    pillBorder: "#ccc",
    pillActiveBg: "#fff",
    pillActiveBorder: "#F4A261",
  },
  {
    key: "dispatcherRides",
    label: "Dispatcher Rides",
    borderColor: "#E76F51",
    bgColor: "rgba(231, 111, 81, 0.25)",
    pillBg: "#fff",
    pillBorder: "#ccc",
    pillActiveBg: "#E76F51",
    pillActiveBorder: "#E76F51",
    pillActiveColor: "#fff",
  },
  {
    key: "streetRides",
    label: "Street Rides",
    borderColor: "#2EC4B6",
    bgColor: "rgba(46, 196, 182, 0.25)",
    pillBg: "#fff",
    pillBorder: "#ccc",
    pillActiveBg: "#2EC4B6",
    pillActiveBorder: "#2EC4B6",
    pillActiveColor: "#fff",
  },
];

const TripSummaryChart = ({ data }) => {
  const [activeCategories, setActiveCategories] = useState(["appRides"]);

  const toggleCategory = (key) => {
    setActiveCategories((prev) => {
      if (prev.includes(key)) {
        // Don't allow deselecting all
        if (prev.length === 1) return prev;
        return prev.filter((k) => k !== key);
      }
      return [...prev, key];
    });
  };

  const chartData = useMemo(() => {
    if (!data || !data.labels || data.labels.length === 0) {
      return null;
    }

    const formattedLabels = data.labels.map((label) =>
      moment(label).format("DD MMM YYYY")
    );

    const datasets = CATEGORIES.filter((cat) =>
      activeCategories.includes(cat.key)
    ).map((cat) => ({
      label: cat.label,
      data: data[cat.key] || [],
      borderColor: cat.borderColor,
      backgroundColor: cat.bgColor,
      fill: true,
      tension: 0.4,
      pointRadius: 5,
      pointBackgroundColor: "#fff",
      pointBorderColor: cat.borderColor,
      pointBorderWidth: 2,
      pointHoverRadius: 7,
      pointHoverBackgroundColor: cat.borderColor,
      pointHoverBorderColor: "#fff",
      pointHoverBorderWidth: 2,
      borderWidth: 2.5,
    }));

    return {
      labels: formattedLabels,
      datasets,
    };
  }, [data, activeCategories]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: "index",
      intersect: false,
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: "#fff",
        titleColor: "#333",
        bodyColor: "#555",
        borderColor: "#e0e0e0",
        borderWidth: 1,
        padding: 12,
        cornerRadius: 8,
        titleFont: { size: 13, weight: "600" },
        bodyFont: { size: 12 },
        boxPadding: 6,
        usePointStyle: true,
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: "#888",
          font: { size: 11 },
          maxRotation: 45,
          minRotation: 0,
        },
        border: {
          color: "#e0e0e0",
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          color: "rgba(0, 0, 0, 0.06)",
          drawBorder: false,
          borderDash: [4, 4],
        },
        ticks: {
          color: "#888",
          font: { size: 11 },
          stepSize: 100,
        },
        border: {
          display: false,
        },
      },
    },
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h3 style={styles.title}>Trip Summary</h3>
        <div style={styles.pillContainer}>
          {CATEGORIES.map((cat) => {
            const isActive = activeCategories.includes(cat.key);
            return (
              <button
                key={cat.key}
                onClick={() => toggleCategory(cat.key)}
                style={{
                  ...styles.pill,
                  backgroundColor: isActive
                    ? cat.pillActiveBg || cat.pillBg
                    : cat.pillBg,
                  borderColor: isActive
                    ? cat.pillActiveBorder
                    : cat.pillBorder,
                  color: isActive
                    ? cat.pillActiveColor || cat.borderColor
                    : "#555",
                }}
              >
                {cat.label}
              </button>
            );
          })}
        </div>
      </div>
      <div style={styles.chartWrapper}>
        {chartData ? (
          <Line data={chartData} options={options} />
        ) : (
          <div style={styles.noData}>No trip data available</div>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: {
    backgroundColor: "#fff",
    borderRadius: 12,
    padding: "24px 28px",
    boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
    marginTop: 24,
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: 16,
    marginBottom: 20,
    flexWrap: "wrap",
  },
  title: {
    fontSize: 20,
    fontWeight: 700,
    color: "#222",
    margin: 0,
  },
  pillContainer: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
  },
  pill: {
    padding: "6px 18px",
    borderRadius: 20,
    border: "1.5px solid #ccc",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 500,
    transition: "all 0.2s ease",
    outline: "none",
    fontFamily: "inherit",
  },
  chartWrapper: {
    height: 320,
    position: "relative",
  },
  noData: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    color: "#999",
    fontSize: 15,
  },
};

export default TripSummaryChart;
